import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { api } from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { Search, MapPin, Phone, MessageCircle, Send, ChevronRight, ChevronLeft, Star, Shield, Users, Zap, Heart, TrendingUp, CheckCircle, Calendar } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AgeVerification from "@/components/AgeVerification";
import SEOHead, { generateOrganizationSchema } from "@/components/SEOHead";

// Premium Ad Card Component with auto slideshow for featured ads
const HomePremiumAdCard = ({ ad, navigate, isGlobalPinned = false, freeAdsAsTop = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Determine if this ad should display as a "top" ad
  const displayAsTop = isGlobalPinned || (ad.is_free && freeAdsAsTop);
  const showBlur = ad.is_free && !freeAdsAsTop;
  const showContactButtons = !ad.is_free || freeAdsAsTop;

  // Auto slideshow for global pinned ads OR free ads displayed as top
  useEffect(() => {
    if (displayAsTop && ad.images && ad.images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % ad.images.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [displayAsTop, ad.images]);

  const nextImage = (e) => {
    e.stopPropagation();
    if (ad.images && ad.images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % ad.images.length);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (ad.images && ad.images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + ad.images.length) % ad.images.length);
    }
  };

  return (
    <Card
      data-testid={`ad-card-${ad.id}`}
      className={`bg-white overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer border-0 ${
        displayAsTop ? 'ring-2 ring-yellow-400/50 shadow-lg shadow-yellow-500/20' : 'shadow-lg'
      }`}
      onClick={() => navigate(`/ad/${ad.id}`)}
    >
      <div className="flex">
        {/* Left: 9:16 Portrait Photo */}
        <div className={`relative flex-shrink-0 bg-gradient-to-br from-gray-100 to-gray-200 ${
          displayAsTop ? 'w-36 sm:w-44 md:w-48' : 'w-32 sm:w-40 md:w-44'
        }`}>
          <div className={`relative ${displayAsTop ? 'aspect-[9/16]' : 'aspect-[9/14]'} overflow-hidden`}>
            {ad.images && ad.images.length > 0 ? (
              <>
                <img
                  src={ad.images[currentImageIndex]}
                  alt={ad.title}
                  className={`w-full h-full object-cover transition-all duration-500 ${showBlur ? 'blur-md' : ''}`}
                />
                
                {/* Image Navigation for multiple images */}
                {ad.images.length > 1 && !showBlur && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    
                    {/* Image dots indicator */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                      {ad.images.map((_, i) => (
                        <span
                          key={i}
                          className={`w-1.5 h-1.5 rounded-full transition-all ${
                            i === currentImageIndex ? 'bg-white' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-4xl bg-gradient-to-br from-pink-100 to-purple-100">
                📸
              </div>
            )}
            
            {/* Free ad overlay - only show if blur is active */}
            {showBlur && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-3xl mb-1">🔒</div>
                  <p className="text-white text-xs font-medium">Click to View</p>
                </div>
              </div>
            )}
            
            {/* TOP badge - show for global pinned OR free ads displayed as top */}
            {displayAsTop && (
              <div className="absolute top-2 left-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-[10px] sm:text-xs px-2 py-1 rounded-full flex items-center gap-1 font-bold shadow-lg">
                <Star className="w-3 h-3" /> TOP
              </div>
            )}
            
            {/* Free badge - only show when not displayed as top */}
            {ad.is_free && !freeAdsAsTop && (
              <div className="absolute top-2 right-2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                FREE
              </div>
            )}
          </div>
        </div>

        {/* Right: Content */}
        <div className="flex-1 p-3 sm:p-4 flex flex-col min-w-0 group">
          {/* Title */}
          <h3 className={`font-bold text-pink-600 mb-2 line-clamp-2 leading-tight ${
            displayAsTop ? 'text-base sm:text-lg' : 'text-sm sm:text-base'
          }`}>
            {ad.title}
          </h3>
          
          {/* Description */}
          <p className={`text-gray-600 mb-3 line-clamp-3 flex-grow ${
            displayAsTop ? 'text-sm' : 'text-xs sm:text-sm'
          }`}>
            {ad.description}
          </p>
          
          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-3">
            {ad.age && (
              <span className="flex items-center gap-0.5 bg-gray-100 px-2 py-1 rounded-full">
                <Calendar className="w-3 h-3" /> {ad.age} Yrs
              </span>
            )}
            <span className="flex items-center gap-0.5 bg-gray-100 px-2 py-1 rounded-full">
              <MapPin className="w-3 h-3" /> 
              {ad.is_global_pinned ? "India" : (ad.city_names?.[0] || 'India')}
            </span>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end gap-2 mt-auto">
            {showContactButtons ? (
              <>
                {ad.contact_phone && (
                  <Button
                    size="sm"
                    className="h-10 w-10 sm:h-11 sm:w-11 p-0 bg-pink-50 hover:bg-pink-100 text-pink-600 rounded-xl border border-pink-200 shadow-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.href = `tel:${ad.contact_phone}`;
                    }}
                  >
                    <Phone className="w-5 h-5" />
                  </Button>
                )}
                {ad.contact_whatsapp && (
                  <Button
                    size="sm"
                    className="h-10 w-10 sm:h-11 sm:w-11 p-0 bg-green-500 hover:bg-green-600 text-white rounded-xl shadow-md shadow-green-500/30"
                    onClick={(e) => {
                      e.stopPropagation();
                      const message = encodeURIComponent(`Hi, I saw your ad "${ad.title}" on uplino.in and I'm interested.`);
                      window.open(`https://wa.me/${ad.contact_whatsapp}?text=${message}`, '_blank');
                    }}
                  >
                    <MessageCircle className="w-5 h-5" />
                  </Button>
                )}
                {ad.contact_telegram && (
                  <Button
                    size="sm"
                    className="h-10 w-10 sm:h-11 sm:w-11 p-0 bg-blue-500 hover:bg-blue-600 text-white rounded-xl shadow-md shadow-blue-500/30"
                    onClick={(e) => {
                      e.stopPropagation();
                      const telegramId = ad.contact_telegram.replace(/[^0-9a-zA-Z_]/g, '');
                      const isUsername = isNaN(telegramId);
                      if (isUsername) {
                        window.open(`https://t.me/${telegramId}`, '_blank');
                      } else {
                        window.open(`https://t.me/+${telegramId}`, '_blank');
                      }
                    }}
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                )}
              </>
            ) : (
              <Button
                size="sm"
                className="h-9 px-4 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white text-xs font-semibold rounded-lg shadow-md"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/ad/${ad.id}`);
                }}
              >
                View Details
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [ads, setAds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [popularCategories, setPopularCategories] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [showMoreCities, setShowMoreCities] = useState(false);
  const [showMoreSearches, setShowMoreSearches] = useState(false);
  const [popularCategoryCities, setPopularCategoryCities] = useState([]);

  const [freeAdsAsTop, setFreeAdsAsTop] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // SEO Schema for homepage
  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      generateOrganizationSchema(),
      {
        "@type": "WebSite",
        "name": "uplino.in",
        "url": "https://uplino.in",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://uplino.in/{city}/{service}",
          "query-input": "required name=city required name=service"
        }
      }
    ]
  };

  const loadData = async () => {
    try {
      const [categoriesRes, popularRes, adsRes] = await Promise.all([
        api.get("/categories"),
        api.get("/home/popular"),
        api.get("/ads?limit=5"),
      ]);
      setCategories(categoriesRes.data);
      setCities(popularRes.data.popular_cities || []);
      setAds(adsRes.data);
      setPopularCategories(popularRes.data.popular_categories || []);
      setAllCities(popularRes.data.all_cities || []);
      setAllCategories(popularRes.data.all_categories || []);
      setPopularCategoryCities(popularRes.data.popular_category_cities || []);
    } catch (error) {
      console.error("Failed to load data", error);
    }
  };

  const loadFreeAdsAsTopSetting = async () => {
    try {
      const response = await api.get("/settings/free-ads-as-top");
      setFreeAdsAsTop(response.data.free_ads_as_top || false);
    } catch (error) {
      console.error("Failed to load free ads as top setting:", error);
    }
  };

  useEffect(() => {
    loadData();
    loadFreeAdsAsTopSetting();
  }, []);

  const handleSearch = () => {
    if (searchQuery) {
      navigate(`/search?q=${searchQuery}`);
    }
  };

  // Separate global pinned ads from regular ads
  const globalPinnedAds = ads.filter(ad => ad.is_global_pinned);
  const regularAds = ads.filter(ad => !ad.is_global_pinned);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* SEO Head */}
      <SEOHead
        title="uplino.in - Premium Adult Services in India | Escorts, Call Girls, Massage"
        description="India's leading classified ads platform for premium adult services. Find verified escorts, call girls, massage services in Mumbai, Delhi, Bangalore & all major cities."
        keywords={[
          'escort service india',
          'call girls india', 
          'massage service india',
          'adult services',
          'escorts mumbai',
          'call girls delhi',
          'massage bangalore',
          'uplino'
        ]}
        canonical="https://uplino.in/"
        schema={homeSchema}
      />
      
      <AgeVerification />
      <Header />

      {/* Hero Section (Premium) */}
      <div className="relative overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1603318445170-10ec9f606980?auto=format&fit=crop&w=2400&q=80)",
          }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/70 to-[#0b0b14]" />
        <div className="absolute inset-0 bg-gradient-to-r from-pink-600/10 via-purple-600/10 to-blue-600/10" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-md border border-white/10 rounded-full mb-6">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-sm text-gray-200 font-medium">Live listings across India</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              <span className="text-white">Discover </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-400 to-pink-400">
                premium
              </span>
              <span className="text-white"> services</span>
            </h1>

            <p className="text-base md:text-lg text-gray-200/90 mb-8 max-w-2xl mx-auto">
              Browse verified profiles by city and category.
            </p>

            {/* Search */}
            <div className="max-w-2xl mx-auto mb-6">
              <div className="flex gap-2 p-2 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
                <Input
                  data-testid="home-search-input"
                  type="text"
                  placeholder="Search by city or service..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  className="h-12 text-base md:text-lg bg-transparent border-0 text-white placeholder:text-gray-300/70 focus-visible:ring-0"
                />
                <Button
                  data-testid="home-search-button"
                  onClick={handleSearch}
                  className="h-12 px-6 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Search
                </Button>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-white text-black hover:bg-white/90 px-10"
                onClick={() => {
                  const el = document.getElementById("browse");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                Browse Listings
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 px-10"
                onClick={() => navigate(user ? "/post-ad" : "/register")}
              >
                Post Your Ad
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-6 sm:gap-12">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white">100+</div>
                <div className="text-sm text-gray-300/70">Cities</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white">24/7</div>
                <div className="text-sm text-gray-300/70">Available</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white">Private</div>
                <div className="text-sm text-gray-300/70">& Discreet</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="border-y border-white/10 bg-[#0b0b14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3">
              <Shield className="w-5 h-5 text-green-400" />
              <div>
                <div className="text-sm font-semibold text-white">Verified</div>
                <div className="text-xs text-gray-400">Profiles & listings</div>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3">
              <CheckCircle className="w-5 h-5 text-blue-400" />
              <div>
                <div className="text-sm font-semibold text-white">Discreet</div>
                <div className="text-xs text-gray-400">Privacy-first browsing</div>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3">
              <Zap className="w-5 h-5 text-yellow-400" />
              <div>
                <div className="text-sm font-semibold text-white">Instant</div>
                <div className="text-xs text-gray-400">Call/WhatsApp contact</div>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3">
              <Users className="w-5 h-5 text-pink-400" />
              <div>
                <div className="text-sm font-semibold text-white">Popular</div>
                <div className="text-xs text-gray-400">Across 100+ cities</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Spotlight (Premium) */}
      <div id="browse" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">Browse by category</h2>
            <p className="text-gray-400">Explore the hottest areas with top cities per category.</p>
          </div>
          <Button
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            onClick={() => navigate("/india/call-girls")}
          >
            Explore all
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(popularCategoryCities.length ? popularCategoryCities : categories.slice(0, 3).map((c) => ({ category: c, cities: [] }))).map(
            (item, index) => {
              const cat = item.category;
              const catSlug = cat?.slug;
              const catName = cat?.name || "Category";

              const imageBySlug = {
                "call-girls": "https://images.unsplash.com/photo-1763336016192-c7b62602e993?auto=format&fit=crop&w=1200&q=80",
                "escort-service": "https://images.unsplash.com/photo-1770235622504-3851a96ac6ef?auto=format&fit=crop&w=1200&q=80",
                "massage-service": "https://images.unsplash.com/photo-1630835474626-b4de96a25186?auto=format&fit=crop&w=1200&q=80",
              };

              const bg = imageBySlug[catSlug] || "https://images.unsplash.com/photo-1603318445170-10ec9f606980?auto=format&fit=crop&w=1200&q=80";

              return (
                <Card
                  key={cat?.id || index}
                  className="group relative overflow-hidden border-white/10 bg-white/5 backdrop-blur-sm hover:border-pink-500/40 transition-all cursor-pointer"
                  onClick={() => catSlug && navigate(`/india/${catSlug}`)}
                >
                  <div className="absolute inset-0">
                    <img
                      src={bg}
                      alt={catName}
                      className="w-full h-full object-cover opacity-70 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/15" />
                  </div>

                  <div className="relative p-6 min-h-[240px] flex flex-col">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white text-xl font-semibold">{catName}</div>
                        <div className="text-gray-300/80 text-sm">
                          {cat?.active_ads ? `${cat.active_ads} active` : "Top picks"}
                        </div>
                      </div>
                      <div className="px-2 py-1 bg-white/10 text-white text-xs rounded-full border border-white/15">
                        Browse
                      </div>
                    </div>

                    <div className="mt-5 flex flex-wrap gap-2">
                      {(item.cities || []).slice(0, 5).map((city) => (
                        <button
                          key={city.id}
                          className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/10 hover:bg-white/15 text-white border border-white/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/${city.slug}/${catSlug}`);
                          }}
                        >
                          {city.name}
                        </button>
                      ))}

                      {catSlug && (
                        <button
                          className="px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/india/${catSlug}`);
                          }}
                        >
                          All Cities
                        </button>
                      )}
                    </div>

                    <div className="mt-auto pt-6 text-sm text-white/90 flex items-center gap-1">
                      View listings <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </Card>
              );
            }
          )}
        </div>
      </div>

      {/* Featured strip removed as per requirement */}
      {/* Popular Cities - Enhanced Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">
              Popular Cities
            </h2>
            <p className="text-gray-400">Find services in your city</p>
          </div>
          <button
            onClick={() => setShowMoreCities(!showMoreCities)}
            className="text-pink-400 hover:text-pink-300 flex items-center gap-1 text-sm font-medium"
          >
            {showMoreCities ? 'Show Less' : 'View More'} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {(showMoreCities ? allCities : cities).map((city, index) => (
            <Link
              key={city.id}
              to={`/${city.slug}`}
              data-testid={`city-button-${city.slug}`}
              className="group"
            >
              <div className={`relative p-4 rounded-xl bg-gradient-to-br ${index === 0 ? 'from-pink-500/20 to-purple-500/20 border-pink-500/30' : 'from-white/5 to-white/10 border-white/10'} border hover:border-pink-500/50 transition-all text-center`}>
                <MapPin className={`w-5 h-5 mx-auto mb-2 ${index === 0 ? 'text-pink-400' : 'text-gray-400'} group-hover:text-pink-400 transition-colors`} />
                <span className="text-white font-medium text-sm">{city.name}</span>
                {index === 0 && (
                  <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-pink-500 text-white text-xs rounded-full">
                    Hot
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Ads - Premium Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Latest Listings
            </h2>
            <p className="text-gray-400 text-sm sm:text-base">Fresh profiles added recently</p>
          </div>
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            Live Updates
          </div>
        </div>
        
        {ads.length === 0 ? (
          <Card className="p-8 sm:p-12 bg-white/5 backdrop-blur-sm border-white/10 text-center">
            <TrendingUp className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400 text-base sm:text-lg mb-2">No listings yet</p>
            <p className="text-gray-500 text-sm mb-6">Be the first to post an ad!</p>
            <Button 
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
            >
              Post Your Ad Free
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Regular Ads */}
            {regularAds.length > 0 && (
              <div className="space-y-3">
                {regularAds.slice(0, 5).map((ad) => (
                  <HomePremiumAdCard
                    key={ad.id}
                    ad={ad}
                    navigate={navigate}
                    isGlobalPinned={false}
                    freeAdsAsTop={freeAdsAsTop}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* CTA Section - Enhanced */}
      {!user && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="relative overflow-hidden rounded-3xl">
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-purple-600 to-pink-600"></div>
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>
            
            <div className="relative px-8 py-16 sm:px-16 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
                <Zap className="w-4 h-4 text-yellow-300" />
                <span className="text-white text-sm font-medium">Get 2 FREE Ads on Sign Up!</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Start Posting Your Ads Today
              </h2>
              <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
                Join 10,000+ advertisers. Get instant visibility across 100+ cities.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  data-testid="cta-register-button"
                  size="lg"
                  className="bg-white text-purple-600 hover:bg-gray-100 px-8 font-semibold"
                  onClick={() => navigate('/register')}
                >
                  Sign Up Free
                </Button>
                <Button
                  data-testid="cta-login-button"
                  size="lg"
                  variant="outline"
                  className="border-white/50 text-white hover:bg-white/20"
                  onClick={() => navigate('/login')}
                >
                  I Have an Account
                </Button>
              </div>
              
              <div className="flex flex-wrap justify-center gap-6 mt-8 text-white/70 text-sm">
                <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" /> No credit card required</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" /> 2 free ads included</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Instant approval</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Popular Searches (Top Categories) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-white/10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">10 Popular Searches</h2>
          <button
            onClick={() => setShowMoreSearches(!showMoreSearches)}
            className="text-pink-400 hover:text-pink-300 flex items-center gap-1 text-sm font-medium"
          >
            {showMoreSearches ? 'Show Less' : 'View More'} <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {(showMoreSearches ? allCategories : popularCategories).map((cat) => (
            <button
              key={cat.id}
              onClick={() => navigate(`/india/${cat.slug}`)}
              className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-sm text-gray-300 hover:text-white transition-colors"
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* SEO text (collapsed by default) */}
        <div className="mt-12">
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <details>
              <summary className="cursor-pointer text-white font-semibold">About uplino.in</summary>
              <div className="mt-3 text-gray-400 text-sm sm:text-base">
                Browse premium listings across India. Filter by city or explore categories.
              </div>
            </details>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
